const JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json"
}

async function parseResponse(response) {
  if (response.status === 204) {
    return null
  }

  const payload = await response.json().catch(() => ({}))

  if (!response.ok) {
    const error = new Error(payload.error || `Request failed with status ${response.status}`)
    if (payload && typeof payload.code === "string") {
      error.code = payload.code
    }
    error.status = response.status
    throw error
  }

  return payload
}

async function request(path, { method = "GET", body, token } = {}) {
  const response = await fetch(path, {
    method,
    headers: {
      ...JSON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  })

  return parseResponse(response)
}

async function multipartRequest(path, { method = "POST", body, token } = {}) {
  const response = await fetch(path, {
    method,
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body
  })

  return parseResponse(response)
}

export const api = {
  createSession(session) {
    return request("/api/session", {
      method: "POST",
      body: { session }
    })
  },

  getMe(token) {
    return request("/api/me", { token })
  },

  updateMe(token, me) {
    return request("/api/me", {
      method: "PATCH",
      token,
      body: { me }
    })
  },

  uploadAvatar(token, file, crop, source) {
    const formData = new FormData()
    formData.append("avatar", file)
    formData.append("crop[x]", String(crop.x))
    formData.append("crop[y]", String(crop.y))
    formData.append("crop[width]", String(crop.width))
    formData.append("crop[height]", String(crop.height))
    formData.append("source[width]", String(source.width))
    formData.append("source[height]", String(source.height))

    return multipartRequest("/api/me/avatar", {
      method: "POST",
      token,
      body: formData
    })
  },

  updateAvatarCrop(token, crop) {
    return request("/api/me/avatar", {
      method: "PATCH",
      token,
      body: { crop }
    })
  },

  deleteAvatar(token) {
    return request("/api/me/avatar", {
      method: "DELETE",
      token
    })
  },

  joinQueue(token) {
    return request("/api/queue", {
      method: "POST",
      token
    })
  },

  leaveQueue(token) {
    return request("/api/queue", {
      method: "DELETE",
      token
    })
  },

  currentCallSession(token) {
    return request("/api/call_sessions/current", { token })
  },

  getRoom(code, token) {
    return request(`/api/rooms/${encodeURIComponent(code)}`, { token })
  },

  getRoomMessages(token, code, beforeId) {
    const search = beforeId ? `?before_id=${encodeURIComponent(beforeId)}` : ""
    return request(`/api/rooms/${encodeURIComponent(code)}/messages${search}`, { token })
  },

  getDirectMessages(token, code, userId, beforeId) {
    const search = beforeId ? `?before_id=${encodeURIComponent(beforeId)}` : ""
    return request(`/api/rooms/${encodeURIComponent(code)}/direct_messages/${encodeURIComponent(userId)}${search}`, { token })
  },

  sendDirectMessage(token, code, userId, body) {
    return request(`/api/rooms/${encodeURIComponent(code)}/direct_messages/${encodeURIComponent(userId)}`, {
      method: "POST",
      token,
      body: { direct_message: { body } }
    })
  },

  createRoom(token, room = {}) {
    return request("/api/rooms", {
      method: "POST",
      token,
      body: { room }
    })
  },

  joinRoom(token, code) {
    return request(`/api/rooms/${encodeURIComponent(code)}/join`, {
      method: "POST",
      token
    })
  },

  leaveRoom(token, code) {
    return request(`/api/rooms/${encodeURIComponent(code)}/leave`, {
      method: "POST",
      token
    })
  },

  getCallSession(token, id) {
    return request(`/api/call_sessions/${id}`, { token })
  },

  nextCallSession(token, id, requeue = true) {
    return request(`/api/call_sessions/${id}/next`, {
      method: "POST",
      token,
      body: { requeue }
    })
  },

  createReport(token, callSessionId, report) {
    return request(`/api/call_sessions/${callSessionId}/reports`, {
      method: "POST",
      token,
      body: { report }
    })
  },

  lockRoom(token, code) {
    return request(`/api/rooms/${encodeURIComponent(code)}/lock`, {
      method: "POST",
      token
    })
  },

  unlockRoom(token, code) {
    return request(`/api/rooms/${encodeURIComponent(code)}/unlock`, {
      method: "POST",
      token
    })
  },

  kickMember(token, code, userId) {
    return request(`/api/rooms/${encodeURIComponent(code)}/kick`, {
      method: "POST",
      token,
      body: { user_id: userId }
    })
  },

  muteMember(token, code, userId, muted = true) {
    return request(`/api/rooms/${encodeURIComponent(code)}/mute`, {
      method: "POST",
      token,
      body: { user_id: userId, muted }
    })
  },

  approveMember(token, code, userId) {
    return request(`/api/rooms/${encodeURIComponent(code)}/approve`, {
      method: "POST",
      token,
      body: { user_id: userId }
    })
  },

  denyMember(token, code, userId) {
    return request(`/api/rooms/${encodeURIComponent(code)}/deny`, {
      method: "POST",
      token,
      body: { user_id: userId }
    })
  }
}
